import React, { useCallback, useMemo } from "react";
import { useLocation, navigate } from "@reach/router";
import { SettingsPageLayout } from "newui/features/Settings/components/SettingsPageLayout";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import type { Company } from "newui/features/Company/types";
import { ScoringInstructions } from "./components/ScoringInstructions/ScoringInstructions";
import { EvaluationPermissions } from "./components/EvaluationPermissions/EvaluationPermissions";
import { PanelDisclaimerSettings } from "./components/PanelDisclaimerSettings";
import { QuoteTemplateSettings } from "newui/features/Rfx/components/QuoteTable/QuoteTableSettings";
import { CompanyScheduleSettings } from "../Rfx/components/ScheduleOfRates";
import { SubmissionComplianceTab } from "./SubmissionComplianceTab";
import {
  useRfxSettingsTabs,
  TAB_MODULE_KEY,
  SCORING_INSTRUCTIONS,
  EVALUATION_PERMISSIONS,
  SCHEDULE_OF_RATES,
  QUOTE_TEMPLATES,
  PANEL_MEMBER_DISCLAIMER,
  SUBMISSION_COMPLIANCE,
} from "./hooks/useRfxSettingsTabs";
import { NoAccessMessage, ReadOnlySurface } from "newui/components/AccessGate";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface RfxSettingsProps {
  company: Company;
  /** Path prop is consumed by @reach/router; not used directly. */
  path?: string;
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const SLUG_TO_NAME: Record<string, string> = {
  "scoring-instructions": SCORING_INSTRUCTIONS,
  "evaluation-permissions": EVALUATION_PERMISSIONS,
  "schedule-of-rates": SCHEDULE_OF_RATES,
  "quote-templates": QUOTE_TEMPLATES,
  "panel-member-disclaimer": PANEL_MEMBER_DISCLAIMER,
  "submission-compliance": SUBMISSION_COMPLIANCE,
};

const NAME_TO_SLUG: Record<string, string> = Object.entries(SLUG_TO_NAME).reduce(
  (acc, [slug, name]) => ({ ...acc, [name]: slug }),
  {}
);


/* -------------------------------------------------------------------------- */
/* URL parsing                                                                */
/* -------------------------------------------------------------------------- */

interface ParsedRoute {
  basePath: string; // everything up to and including /rfx-settings
  editingId: number | null;
  isNew: boolean;
  tabSlug: string | null;
}

const parseRoute = (pathname: string): ParsedRoute => {
  const marker = "/rfx-settings";
  const idx = pathname.indexOf(marker);
  if (idx === -1) {
    return { basePath: pathname.replace(/\/$/, ""), tabSlug: null, editingId: null, isNew: false };
  }
  const basePath = pathname.slice(0, idx + marker.length);
  const remainder = pathname.slice(idx + marker.length).replace(/^\/+|\/+$/g, "");
  if (!remainder) {
    return { basePath, tabSlug: null, editingId: null, isNew: false };
  }
  const [slug, second] = remainder.split("/");
  if (!second) {
    return { basePath, tabSlug: slug, editingId: null, isNew: false };
  }
  if (second === "new") {
    return { basePath, tabSlug: slug, editingId: null, isNew: true };
  }
  const parsed = Number(second);
  return {
    basePath,
    tabSlug: slug,
    editingId: Number.isFinite(parsed) ? parsed : null,
    isNew: false,
  };
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export const RfxSettings = ({ company }: RfxSettingsProps) => {
  const location = useLocation();

  /*
   * The SAME hook the Settings menu uses to decide whether to offer this page
   * at all. Before PRO-709's follow-up the two computed their answers
   * separately and disagreed, which is how a visible menu item could land on
   * the "no access" screen below.
   */
  const { tabs, accessByModule, isLoading: isPermissionsLoading } = useRfxSettingsTabs();

  const defaultTab = tabs[0]?.name ?? SCORING_INSTRUCTIONS;
  const defaultSlug = tabs[0]?.id ?? "scoring-instructions";

  const parsed = useMemo(() => parseRoute(location.pathname), [location.pathname]);

  // Resolve the active tab from the URL, falling back to the first available tab.
  const resolvedTabName = useMemo(() => {
    if (parsed.tabSlug && SLUG_TO_NAME[parsed.tabSlug]) {
      const name = SLUG_TO_NAME[parsed.tabSlug];
      // Only honour the slug if it belongs to the current profile's tabs.
      if (tabs.some((t) => t.name === name)) {
        return name;
      }
    }
    return defaultTab;
  }, [parsed.tabSlug, tabs, defaultTab]);

  const changeActiveTab = useCallback(
    (name: string): void => {
      const slug = NAME_TO_SLUG[name] ?? defaultSlug;
      navigate(`${parsed.basePath}/${slug}`);
    },
    [parsed.basePath, defaultSlug]
  );

  const handleQuoteTemplateEditingChange = useCallback(
    (id: number | null, opts?: { isNew?: boolean }) => {
      const base = `${parsed.basePath}/quote-templates`;
      if (opts?.isNew) {
        navigate(`${base}/new`);
        return;
      }
      navigate(id == null ? base : `${base}/${id}`);
    },
    [parsed.basePath]
  );

  const isActiveTabReadOnly =
    accessByModule[TAB_MODULE_KEY[resolvedTabName]]?.canEdit !== true;

  if (isPermissionsLoading) {
    return <LoadingSpinner label="Loading RFx Settings..." verticalPadding={8} />;
  }

  if (tabs.length === 0) {
    return (
      <NoAccessMessage
        data-testid="rfx-settings-no-access"
        description="You do not have permission to view RFx Settings. Ask a company administrator if you need access."
      />
    );
  }

  const activeTabContent = (
    <>
      {resolvedTabName === SCORING_INSTRUCTIONS && (
        <ScoringInstructions company={company} />
      )}
      {resolvedTabName === EVALUATION_PERMISSIONS && (
        <EvaluationPermissions company={company} />
      )}
      {resolvedTabName === SCHEDULE_OF_RATES && company?.id && (
        <CompanyScheduleSettings companyId={company.id} />
      )}
      {resolvedTabName === QUOTE_TEMPLATES && company?.id && (
        <QuoteTemplateSettings
          companyId={company.id}
          editingId={parsed.editingId}
          isCreatingNew={parsed.isNew}
          onEditingChange={handleQuoteTemplateEditingChange}
        />
      )}
      {resolvedTabName === PANEL_MEMBER_DISCLAIMER && (
        <PanelDisclaimerSettings company={company} />
      )}
      {resolvedTabName === SUBMISSION_COMPLIANCE && (
        <SubmissionComplianceTab company={company} />
      )}
    </>
  );

  return (
    <SettingsPageLayout
      heading="RFx Settings"
      tabs={tabs}
      activeTab={resolvedTabName}
      onTabChange={changeActiveTab}
    >
      {isActiveTabReadOnly ? (
        <ReadOnlySurface
          data-testid="rfx-settings-read-only"
          message="You have read-only access to this tab. Saving, creating and deleting are disabled."
        >
          {activeTabContent}
        </ReadOnlySurface>
      ) : (
        activeTabContent
      )}
    </SettingsPageLayout>
  );
};
